import React, {createRef} from 'react'
import {findDOMNode} from "react-dom"

import {InitialEditorStateType, LexicalComposer} from "@lexical/react/LexicalComposer"
import {ContentEditable} from "@lexical/react/LexicalContentEditable"
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary"
import {HistoryPlugin} from "@lexical/react/LexicalHistoryPlugin"
import {RichTextPlugin} from "@lexical/react/LexicalRichTextPlugin"
import {$getRoot, CLEAR_EDITOR_COMMAND, EditorState, LexicalEditor} from "lexical"
import {ListPlugin} from "@lexical/react/LexicalListPlugin"
import ToolBarPlugin from "./plugins/toolbar/toolbar"
import {TextareaTheme} from "./theme"
import {TextareaNodes} from "./nodes"
import {HorizontalRulePlugin} from "@lexical/react/LexicalHorizontalRulePlugin"
import LinkPlugin from "./plugins/link"
import FloatingLinkEditorPlugin from "./plugins/floating-link-editor"
import AutoLinkPlugin from "./plugins/auto-link"
import ImagesPlugin from "./plugins/image"
import SlashMenuPlugin from "./plugins/slash-menu"
import MentionsPlugin from "./plugins/mentions"
import {CheckListPlugin} from "@lexical/react/LexicalCheckListPlugin"
import {BannerPlugin} from "./plugins/banner/banner-plugin"
import {BadgePlugin} from "./plugins/highlight/badge/badge-plugin"
import {FontColorPlugin} from "./plugins/highlight/font-color/font-color-plugin"
import PageBreakPlugin from "./plugins/page-break"
import EmojisPlugin from "./plugins/emoji"
import EmojiPickerPlugin from "./plugins/emoji/picker"
import {TableContext, TablePlugin as TableNewPlugin} from "./plugins/table"
import {TableCellNodes} from "./plugins/table/nodes"
import {LayoutPlugin} from "./plugins/layout"
import CollapsiblePlugin from "./plugins/collapse-container"
import YouTubePlugin from "./plugins/embed/youtube"
import FigmaPlugin from "./plugins/embed/figma"
import AutoEmbedPlugin from "./plugins/embed"
import LexicalEditorLocalSettings from "./settings/local-settings"
import CodeHighlightPlugin from "./plugins/code/highlight"
import CodeActionMenuPlugin from "./plugins/code/action-menu"
import DraggableBlockPlugin from "./plugins/draggable-block"
import {AutoFocusPlugin} from "@lexical/react/LexicalAutoFocusPlugin"
import AttachmentsPlugin, {INSERT_ATTACHMENT_COMMAND} from "./plugins/attachment"
import Attachment from "../../model2/attachment/attachment"
import AttachmentsCollection from "../../model2/attachment/attachments"
import {ClearEditorPlugin} from "@lexical/react/LexicalClearEditorPlugin"
import LexicalClickableLinkPlugin from "@lexical/react/LexicalClickableLinkPlugin"
import {EditorRefPlugin} from "@lexical/react/LexicalEditorRefPlugin"
import {lexicalAddLink} from "./actions/insert-link"
import {lexicalAddText} from "./actions/insert-text"
import {TabIndentationPlugin} from "@lexical/react/LexicalTabIndentationPlugin"
import {EntitySelectorEntityType} from "../entity-selector/selector"
import ListMaxIndentLevelPlugin from "./plugins/max-indent-level"
import CommandsPlugin from "./plugins/commands"
import {lexicalFindMentions} from "./actions/find-mentions"
import omitEmpty from "../../gd/utils/misc/omit-empty"
import OnChangePlugin from "./plugins/on-change"
import type {NodeKey} from "lexical/LexicalNode"
import AttachmentsContext from "./plugins/attachment/context"
import EditorStateUpdate from "./updaters/editor-state-update"
import {lexicalGetInitialEditorState} from "./actions/get-initial-editor-state"
import HScroll from "../scrolling/custom-h-scroll/h-scroll"
import {MarkdownShortcutPlugin} from "@lexical/react/LexicalMarkdownShortcutPlugin"
import {lexicalGetTransformers} from "./actions/get-transformers"
import FloatingTextFormatToolbarPlugin from "./plugins/floating-format-toolbar/format-toolbar"
import {ContentRTF, RTFVersion} from "./@type";

type Props = {
    className?                          : string
    mentions?                           : any
    projectId?                          : ID
    attachmentsCollection?              : AttachmentsCollection // is used in attachments plugin
    placeholder?                        : string
    messageText?                        : string                // is used if no messageRTF
    messageRTF?                         : ContentRTF

    attachmentInMessageDefaultWidth?    : number | string
    attachmentInEditorIsResizeable?     : boolean
    stateSaveInterval?                  : number
    localSettingsName?                  : string
    localSettings?                      : LexicalEditorLocalSettings

    useLocalSettings?                   : boolean               // for showControls in localSettings // TODO: rename parameter
    focusOnMount?                       : boolean
    editable?                           : boolean
    showControls?                       : boolean
    showUndoRedo?                       : boolean
    showSettingsButton?                 : boolean
    hideControlsAllowed?                : boolean
    // forceComponentsOfViewType?          : boolean

    addField?                           : any                   // used to add "Add field button" used in editor/formulas, see addFieldConfig object
    controlsParams?                     : {
        leftChildren?   : any,
        rightChildren?  : any
    },                                              // used to add buttons to toolbar if it is enabled (showControls)

    onCommandEnterKey?                  : callback
    onKeyDown?                          : callback
    onEscape?                           : callback
    onAddAttachment?                    : callback              // if passed -> will handle select file for attachment and show attachment icon
    onPasteImage?                       : {(file:any):void}
    onChange?                           : {(messageText:string,  messageRTF: ContentRTF):void}

    children?                           : any
}

export type EntitySelectorSettings = {
    entityType?: EntitySelectorEntityType
}

const debug = false
// const debug = "RichEditorLexical"

// https://lexical.dev/docs/getting-started/react
export default class RichEditorLexical extends React.Component<Props, any> {

    localSettings           : LexicalEditorLocalSettings = this.props.localSettings ? this.props.localSettings : new LexicalEditorLocalSettings(this.props.localSettingsName)
    editor?                 : LexicalEditor
    debounceConfirmChange   : any

    entitySelectorSettings  : EntitySelectorSettings = {}   // settings for mentions popover (stores initial menu selected)

    initialEditorState?     : InitialEditorStateType

    hScrollRef              : any

    static defaultProps = {
        useLocalSettings    : true,
        stateSaveInterval   : 0,
        focusOnMount        : true,
        editable            : true,
        showControls        : true,
        showUndoRedo        : true,
        showSettingsButton  : true,
        hideControlsAllowed : true,
    }

    state = {
        floatingAnchorElem  : undefined
    }

    constructor(props:Props) {
        super(props)

        this.hScrollRef = createRef()

        debug && console.log(debug, 'constructor', props)

        this.debounceConfirmChange = _.debounce(this.onConfirmChange, props.stateSaveInterval, {trailing: true})

        this.initialEditorState = lexicalGetInitialEditorState(props.messageText, props.messageRTF)
    }

    componentDidMount() {
        if (this.props.editable) {
            gd.bus.subscribe('lexical-update-editor', this, this.update)
        }
    }

    componentWillUnmount() {
        gd.bus.unsubscribe(this)

        this.debounceConfirmChange.cancel()
    }

    update = () => {
        debug && console.log(debug, 'update')

        this.forceUpdate()
    }

    updateScroll = () => {
        debug && console.log(debug, 'updateScroll')
        setTimeout(()=>{this.hScrollRef.current?.calculateScroll()}, 100)
    }

    getPrintContainer() {
        return findDOMNode(this.state.floatingAnchorElem)
    }

    onEditor = (activeEditor: LexicalEditor) => {
        this.editor = activeEditor
    }

    clear = () => {
        this.editor?.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined)
    }

    focus = () => {this.editor?.focus();}
    blur = () => {/*this.editor?.blur();*/}

    addAttachmentToEditor = (fileData: Attachment) => {
        debug && console.log(debug, 'addAttachmentToEditor', this.editor, fileData)
        this.editor?.dispatchCommand(INSERT_ATTACHMENT_COMMAND, fileData)
    }

    addTextToEditor = (text:string, postfix:string = '') => {
        this.editor?.focus()
        this.editor && lexicalAddText(this.editor, text)
    }

    addLinkToEditor = (label:string, url:string, postfix = '') => {
        this.editor?.focus()
        this.editor && lexicalAddLink(this.editor, url, label)
    }

    onRef = (r: any) => {
        this.hScrollRef.current = r

        const node = findDOMNode(r)
        if (node) {
            this.setState({
                floatingAnchorElem: node
            })
        }
    }

    onChange = (data: {
        prevEditorState     : EditorState,
        editorState         : EditorState,
        editor              : LexicalEditor,
        dirtyElements       : Map<NodeKey, boolean>,
        dirtyLeaves         : Set<NodeKey>,
        normalizedNodes     : Set<NodeKey>,
        tags                : Set<string>
    }) => {
        data.editorState.read(()=>{
            const root:any = $getRoot()
            const isEmpty = !root.getFirstChild() || (root.getFirstChild()?.isEmpty() && root.getChildrenSize() === 1)

            debug && console.log(debug, 'onChange', isEmpty, data)

            if (isEmpty) {
                debug && console.log(debug, 'onChange read isEmpty')
                this.debounceConfirmChange('', null)
            } else {
                const lexicalJSON   = data.editorState.toJSON()
                const messageRTF    = omitEmpty({
                    v               : RTFVersion.LEXICAL,
                    content         : lexicalJSON,
                    importOriginal  : this.props.messageRTF?.importOriginal || undefined,
                    mentions        : lexicalFindMentions(lexicalJSON)
                })
                const messageText   = $getRoot().getTextContent()

                debug && console.log(debug, 'onChange read', messageText, messageRTF)
                this.debounceConfirmChange(messageText, messageRTF)
            }
        })
    }

    onConfirmChange = (messageText:string, messageRTF:any) => {
        // required for scroll to appear if table with lots of columns is added (or disappear if deleted)
        this.updateScroll()

        this.props.onChange && this.props.onChange(messageText, messageRTF)
    }

    onError = (error:any) => {
        console.error(error);
    }

    render() {
        this.localSettings = this.props.localSettings || new LexicalEditorLocalSettings(this.props.localSettingsName)

        const initialConfig = {
            namespace   : 'TextareaEditor',
            theme       : TextareaTheme,
            nodes       : TextareaNodes,
            editable    : this.props.editable,
            editorState : this.initialEditorState,
            onError     : this.onError,
        }

        const cellEditorConfig = {
            namespace   : 'TextareaEditor',
            theme       : TextareaTheme,
            nodes       : TableCellNodes,
            editable    : this.props.editable,
            onError     : this.onError,
        }

        debug && console.log(debug, 'render', this.localSettings, this.entitySelectorSettings, initialConfig, this.props.messageRTF)

        const showControls          = this.props.useLocalSettings ? this.localSettings.showControls : this.props.showControls
        const showFloatingToolbar   = this.props.editable && !showControls
        const hasMentions           = lexicalFindMentions(this.props.messageRTF?.content).length > 0

        const classes = Classes('rich-editor-lexical', 'rte-root', this.props.className, {
            'edit-mode'     : this.props.editable,
            'with-controls' : showControls,
            'with-mentions' : hasMentions
        })

        return (
            <div className={classes}>
                <LexicalComposer initialConfig={initialConfig}>
                    <AttachmentsContext attachmentInMessageDefaultWidth = {this.props.attachmentInMessageDefaultWidth}
                                        attachmentInEditorIsResizeable  = {this.props.attachmentInEditorIsResizeable}>
                        <TableContext>
                            <>
                                <EditorRefPlugin editorRef={this.onEditor}/>
                                <EditorStateUpdate messageRTF   = {this.props.messageRTF}
                                                   messageText  = {this.props.messageText}/>

                                {this.props.editable &&
                                    <ToolBarPlugin localSettings        = {this.localSettings}
                                                   addField             = {this.props.addField}
                                                   controlsParams       = {this.props.controlsParams}
                                                   showControls         = {showControls}
                                                   showUndoRedo         = {this.props.showUndoRedo}
                                                   showSettingsButton   = {this.props.showSettingsButton}
                                                   hideControlsAllowed  = {this.props.hideControlsAllowed}
                                                   onAddAttachment      = {this.props.onAddAttachment}/>}

                                <RichTextPlugin contentEditable = {
                                    <div className="editor-container">
                                        <HScroll ref                    = {this.onRef}
                                                 className              = "editor-content-scroll"
                                                 containerClass         = "editor-content-container"
                                                 initialCalculateDelay  = {1000}>
                                            <ContentEditable id         = "lexical-content-editable"
                                                             className  = "content-editable"/>
                                        </HScroll>
                                    </div>
                                }
                                                placeholder={this.props.placeholder ?
                                                    <div className="placeholder">{this.props.placeholder}</div> : null}
                                                ErrorBoundary   = {LexicalErrorBoundary} />
                                {this.props.focusOnMount && <AutoFocusPlugin/>}
                                <ClearEditorPlugin />
                                <HistoryPlugin />
                                <OnChangePlugin ignoreSelectionChange   = {true}
                                                onChange                = {this.onChange} />
                                <ListPlugin/>
                                <CheckListPlugin />
                                <HorizontalRulePlugin />

                                <LinkPlugin />
                                {!this.props.editable && <LexicalClickableLinkPlugin />}
                                <FloatingLinkEditorPlugin/>
                                {this.localSettings.autolink && <AutoLinkPlugin />}

                                {showFloatingToolbar && <FloatingTextFormatToolbarPlugin anchorElem={this.state.floatingAnchorElem}/>}

                                <ImagesPlugin/>

                                {this.props.editable &&
                                    <>
                                        <SlashMenuPlugin entitySelectorSettings = {this.entitySelectorSettings}
                                                         projectId              = {this.props.projectId}/>
                                        <MentionsPlugin entitySelectorSettings      = {this.entitySelectorSettings}
                                                        mentions                    = {this.props.mentions}
                                                        projectId                   = {this.props.projectId}/>
                                    </>}

                                <BannerPlugin/>
                                <BadgePlugin/>
                                <FontColorPlugin/>

                                <PageBreakPlugin/>

                                <EmojisPlugin/>
                                {this.props.editable && <EmojiPickerPlugin/>}

                                <TableNewPlugin cellEditorConfig={cellEditorConfig}>
                                    <AutoFocusPlugin/>
                                    <RichTextPlugin contentEditable = {<ContentEditable className="table-node-content-editable" />}
                                                    placeholder     = {null}
                                                    ErrorBoundary   = {LexicalErrorBoundary}/>
                                    <MentionsPlugin entitySelectorSettings  = {this.entitySelectorSettings}
                                                    mentions                = {this.props.mentions}
                                                    projectId               = {this.props.projectId}/>
                                    <HistoryPlugin/>
                                    <ImagesPlugin/>
                                    <LinkPlugin/>
                                </TableNewPlugin>

                                <LayoutPlugin/>
                                <CollapsiblePlugin/>
                                <YouTubePlugin/>
                                <FigmaPlugin/>
                                <AutoEmbedPlugin/>

                                <CodeActionMenuPlugin anchorElem    = {this.state.floatingAnchorElem}
                                                      theme         = {cellEditorConfig.theme}/>
                                <CodeHighlightPlugin/>

                                <DraggableBlockPlugin anchorElem={this.state.floatingAnchorElem} />

                                <AttachmentsPlugin attachmentsCollection    = {this.props.attachmentsCollection}
                                                   onPasteImage             = {this.props.onPasteImage}/>

                                <TabIndentationPlugin />
                                <ListMaxIndentLevelPlugin maxDepth={7}/>

                                <CommandsPlugin onKeyDown           = {this.props.onKeyDown}
                                                onEscape            = {this.props.onEscape}
                                                onCommandEnterKey   = {this.props.onCommandEnterKey}/>

                                <MarkdownShortcutPlugin transformers={lexicalGetTransformers()} />
                            </>
                        </TableContext>
                    </AttachmentsContext>
                </LexicalComposer>

                {this.props.children}
            </div>
        )
    }
}
